import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type MutableRefObject,
  type RefObject,
} from 'react'
import type {
  RecordClickEventArgs,
  RowDataBoundEventArgs,
} from '@syncfusion/ej2-grids'
import type {
  ActionEventArgs,
  GridComponent,
  RowSelectEventArgs,
} from '@syncfusion/ej2-react-grids'
import {
  bindChartHistoryRowSelection,
  getChartHistoryRowId,
  resolveChartHistoryAppearanceId,
  selectChartHistoryRow,
  shouldApplyForcedChartHistorySelect,
  shouldSkipForcedChartHistorySelect,
  shouldTakeOverChartHistorySelection,
  syncChartHistoryRowAppearance,
} from '@utils/chart-history-selection.utils'

interface ChartHistoryRow {
  chart_id?: string
}

interface UseChartHistoryGridSelectionParams {
  gridRef: RefObject<GridComponent>
  historyData: ChartHistoryRow[]
  selectedChartId: string
  onSelectHistory: (args: RowSelectEventArgs) => void
  isMountedRef: MutableRefObject<boolean>
  canSyncSelection: boolean
  shouldIgnoreDeselect?: (args: RowSelectEventArgs) => boolean
  getSelectedChartId?: () => string
}

interface UseChartHistoryGridSelectionResult {
  suppressSelectionSyncRef: MutableRefObject<boolean>
  selectChartRowIfAvailable: (chartId: string) => boolean
  clearChartRowSelection: () => void
  handleRowSelected: (args: RowSelectEventArgs) => void
  handleRowDeselected: (args: RowSelectEventArgs) => void
  handleRecordClick: (args: RecordClickEventArgs) => void
  handleRowDataBound: (args: RowDataBoundEventArgs) => void
  handleDataBound: () => void
  handleGridActionComplete: (args: ActionEventArgs) => void
}

export function useChartHistoryGridSelection({
  gridRef,
  historyData,
  selectedChartId,
  onSelectHistory,
  isMountedRef,
  canSyncSelection,
  shouldIgnoreDeselect,
  getSelectedChartId,
}: UseChartHistoryGridSelectionParams): UseChartHistoryGridSelectionResult {
  const suppressSelectionSyncRef = useRef(false)
  const userTookOverSelectionRef = useRef(false)
  const lastUserClickedChartIdRef = useRef('')
  const skipForwardedDeselectRef = useRef(false)
  const selectedChartIdRef = useRef(selectedChartId)
  selectedChartIdRef.current = selectedChartId
  const getSelectedChartIdRef = useRef(getSelectedChartId)
  getSelectedChartIdRef.current = getSelectedChartId

  const resolveSelectedChartId = (): string =>
    (getSelectedChartIdRef.current?.() ?? selectedChartIdRef.current).trim()

  const selectChartRowIfAvailable = useCallback(
    (chartId: string): boolean => {
      const liveId = resolveSelectedChartId()
      if (
        !shouldApplyForcedChartHistorySelect(chartId, liveId) ||
        !isMountedRef.current ||
        !gridRef.current ||
        suppressSelectionSyncRef.current ||
        shouldSkipForcedChartHistorySelect(
          chartId,
          userTookOverSelectionRef.current,
          lastUserClickedChartIdRef.current,
        )
      ) {
        return false
      }
      try {
        return selectChartHistoryRow(gridRef.current, chartId, historyData)
      } catch {
        return false
      }
    },
    [gridRef, historyData, isMountedRef],
  )

  const clearChartRowSelection = useCallback((): void => {
    if (!gridRef.current?.element?.parentNode) {
      return
    }
    try {
      syncChartHistoryRowAppearance(gridRef.current, '')
      suppressSelectionSyncRef.current = true
      gridRef.current.clearSelection()
    } catch {
      // Ignore grid sync errors (unmount / Syncfusion edge cases)
    } finally {
      window.queueMicrotask(() => {
        suppressSelectionSyncRef.current = false
      })
    }
  }, [gridRef])

  useLayoutEffect(() => {
    const id = selectedChartId.trim()
    if (!id) {
      userTookOverSelectionRef.current = false
      lastUserClickedChartIdRef.current = ''
      clearChartRowSelection()
      return
    }
    const lastClicked = lastUserClickedChartIdRef.current
    const tookOver = userTookOverSelectionRef.current
    const appearanceId = resolveChartHistoryAppearanceId(
      id,
      tookOver,
      lastClicked,
    )
    if (gridRef.current?.element?.parentNode) {
      syncChartHistoryRowAppearance(gridRef.current, appearanceId)
    }
    if (tookOver && lastClicked && lastClicked !== id) {
      return
    }
    if (id !== lastClicked) {
      userTookOverSelectionRef.current = false
    }
  }, [clearChartRowSelection, gridRef, selectedChartId])

  const markUserTakeover = (chartId: string): void => {
    lastUserClickedChartIdRef.current = chartId
    userTookOverSelectionRef.current = true
  }

  const handleRowSelected = useCallback(
    (args: RowSelectEventArgs): void => {
      skipForwardedDeselectRef.current = true
      const clickedId = getChartHistoryRowId(args.data)
      if (
        shouldTakeOverChartHistorySelection(
          clickedId,
          selectedChartId,
          Boolean(args.isInteracted),
        )
      ) {
        markUserTakeover(clickedId)
      }
      onSelectHistory(args)
      window.queueMicrotask(() => {
        skipForwardedDeselectRef.current = false
      })
    },
    [onSelectHistory, selectedChartId],
  )

  const handleRowDeselected = useCallback(
    (args: RowSelectEventArgs): void => {
      if (suppressSelectionSyncRef.current) {
        return
      }
      if (shouldIgnoreDeselect?.(args)) {
        return
      }
      if (!args.isInteracted) {
        return
      }
      window.queueMicrotask(() => {
        if (skipForwardedDeselectRef.current) {
          return
        }
        if (suppressSelectionSyncRef.current) {
          return
        }
        markUserTakeover('')
        onSelectHistory(args)
      })
    },
    [onSelectHistory, shouldIgnoreDeselect],
  )

  const handleRecordClick = useCallback(
    (args: RecordClickEventArgs): void => {
      const clickedId = getChartHistoryRowId(args.rowData)
      if (!clickedId) {
        return
      }
      if (clickedId === resolveSelectedChartId()) {
        markUserTakeover('')
        clearChartRowSelection()
        onSelectHistory({
          name: 'rowDeselected',
          data: args.rowData,
          isInteracted: true,
        } as RowSelectEventArgs)
        return
      }
      markUserTakeover(clickedId)
      if (gridRef.current?.element?.parentNode) {
        syncChartHistoryRowAppearance(gridRef.current, clickedId)
      }
      const rowIndex = args.rowIndex
      onSelectHistory({
        name: 'rowSelected',
        data: args.rowData,
        isInteracted: true,
      } as RowSelectEventArgs)
      if (typeof rowIndex !== 'number' || !gridRef.current) {
        return
      }
      window.queueMicrotask(() => {
        if (!gridRef.current) {
          return
        }
        syncChartHistoryRowAppearance(gridRef.current, clickedId)
        const selected =
          (gridRef.current.getSelectedRowIndexes?.() as number[] | undefined) ??
          []
        if (selected[0] === rowIndex) {
          return
        }
        try {
          gridRef.current.selectRow(rowIndex)
          syncChartHistoryRowAppearance(gridRef.current, clickedId)
        } catch {
          // Ignore grid sync errors (unmount / Syncfusion edge cases)
        }
      })
    },
    [clearChartRowSelection, gridRef, onSelectHistory],
  )

  const handleRowDataBound = useCallback(
    (args: RowDataBoundEventArgs): void => {
      const rowChartId = getChartHistoryRowId(args.data)
      bindChartHistoryRowSelection(
        args.row,
        rowChartId,
        resolveChartHistoryAppearanceId(
          resolveSelectedChartId(),
          userTookOverSelectionRef.current,
          lastUserClickedChartIdRef.current,
        ),
      )
    },
    [],
  )

  const handleDataBound = useCallback((): void => {
    if (
      !isMountedRef.current ||
      suppressSelectionSyncRef.current ||
      !canSyncSelection ||
      !gridRef.current
    ) {
      return
    }
    const chartId = resolveSelectedChartId()
    if (!chartId) {
      clearChartRowSelection()
      return
    }
    syncChartHistoryRowAppearance(
      gridRef.current,
      resolveChartHistoryAppearanceId(
        chartId,
        userTookOverSelectionRef.current,
        lastUserClickedChartIdRef.current,
      ),
    )
    selectChartRowIfAvailable(chartId)
  }, [
    canSyncSelection,
    clearChartRowSelection,
    gridRef,
    isMountedRef,
    selectChartRowIfAvailable,
  ])

  const handleGridActionComplete = useCallback(
    (args: ActionEventArgs): void => {
      const requestType = String(args.requestType ?? '')
      const shouldSyncSelection =
        requestType === 'refresh' ||
        requestType === 'paging' ||
        requestType === 'sorting' ||
        requestType === 'filtering'
      if (!shouldSyncSelection || suppressSelectionSyncRef.current) {
        return
      }
      const chartId = resolveSelectedChartId()
      if (!chartId) {
        clearChartRowSelection()
        return
      }
      if (gridRef.current?.element?.parentNode) {
        syncChartHistoryRowAppearance(
          gridRef.current,
          resolveChartHistoryAppearanceId(
            chartId,
            userTookOverSelectionRef.current,
            lastUserClickedChartIdRef.current,
          ),
        )
      }
      selectChartRowIfAvailable(chartId)
    },
    [clearChartRowSelection, gridRef, selectChartRowIfAvailable],
  )

  useEffect(() => {
    let cancelled = false
    let attempt = 0
    let retryTimer: number | undefined
    const maxAttempts = 8
    const run = (): void => {
      if (cancelled || !isMountedRef.current) {
        return
      }
      const chartId = resolveSelectedChartId()
      if (!chartId) {
        userTookOverSelectionRef.current = false
        lastUserClickedChartIdRef.current = ''
        clearChartRowSelection()
        return
      }
      if (
        shouldSkipForcedChartHistorySelect(
          chartId,
          userTookOverSelectionRef.current,
          lastUserClickedChartIdRef.current,
        )
      ) {
        if (gridRef.current?.element?.parentNode) {
          syncChartHistoryRowAppearance(
            gridRef.current,
            resolveChartHistoryAppearanceId(
              chartId,
              userTookOverSelectionRef.current,
              lastUserClickedChartIdRef.current,
            ),
          )
        }
        return
      }
      if (suppressSelectionSyncRef.current) {
        if (attempt < maxAttempts) {
          attempt += 1
          retryTimer = window.setTimeout(run, 32)
        }
        return
      }
      if (
        cancelled ||
        !shouldApplyForcedChartHistorySelect(chartId, resolveSelectedChartId())
      ) {
        return
      }
      const didSelect = selectChartRowIfAvailable(chartId)
      if (didSelect || attempt >= maxAttempts) {
        return
      }
      attempt += 1
      retryTimer = window.setTimeout(run, 32)
    }
    const frameId = window.requestAnimationFrame(run)
    return (): void => {
      cancelled = true
      window.cancelAnimationFrame(frameId)
      if (retryTimer !== undefined) {
        window.clearTimeout(retryTimer)
      }
    }
  }, [
    clearChartRowSelection,
    gridRef,
    historyData,
    isMountedRef,
    selectChartRowIfAvailable,
    selectedChartId,
  ])

  return {
    suppressSelectionSyncRef,
    selectChartRowIfAvailable,
    clearChartRowSelection,
    handleRowSelected,
    handleRowDeselected,
    handleRecordClick,
    handleRowDataBound,
    handleDataBound,
    handleGridActionComplete,
  }
}
